import { trackDesignSystemAction } from 'analytics';
import Icon, { IconType } from 'components/Icon';
import * as Dialog from 'components/Widget/Dialog';
import { useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { RootReducer } from 'redux/reducers';
import { showDsShowcaseModal } from 'redux/mode/actions';
import { createDesignSystemPattern } from 'routes';
import ShowcaseCard from './ShowcaseCard';
import * as S from './styles';

//----------------------------------------------------------------------
// DATA
//----------------------------------------------------------------------

export enum ShowcaseCategory {
	ALL = 'all',
	SAAS = 'saas',
	MARKETING = 'marketing',
	MARKETPLACE = 'marketplace',
	MOBILE = 'mobile',
	ECOMMERCE = 'ecommerce',
	DASHBOARD = 'dashboard',
}

export interface ShowcaseDesignSystem {
	designSystemId: string;
	title: string;
	description: string;
	imageUrl: string;
	hiResImageUrl?: string;
	metadata: string;
	tags: string[];
	category: ShowcaseCategory;
	dsUrl: string;
	projectUrl?: string;
}

// Populate with real data -- each entry maps to an existing DS that will be duplicated on selection
const SHOWCASE_DESIGN_SYSTEMS: ShowcaseDesignSystem[] = [
	{
		designSystemId: '1753b201-ef05-420c-b2af-cb5dd62a689f',
		title: 'Nomad',
		description:
			'An Airbnb-inspired hospitality interface system focused on warm layouts, clear booking flows, and marketplace-style product discovery.',
		imageUrl: '/ds_showcase/nomad.png',
		hiResImageUrl: '/ds_showcase/nomad@2x.png',
		metadata: '22 components · 179 tokens',
		tags: ['hospitality', 'marketplace', 'travel'],
		category: ShowcaseCategory.MARKETPLACE,
		dsUrl: '/ds/1753b201-ef05-420c-b2af-cb5dd62a689f/Nomad',
		projectUrl: '/project/f32d4da4-a654-403f-91d3-3a619b0b8d54/Nomad-Sample-Project',
	},
	{
		designSystemId: 'e254d183-55ee-4b73-8cd6-f3071b26304a',
		title: 'Pixelo',
		description:
			'A warm, modern interface system with editorial typography and earthy tones, designed for contemporary product and marketing surfaces.',
		imageUrl: '/ds_showcase/pixelo.png',
		hiResImageUrl: '/ds_showcase/pixelo@2x.png',
		metadata: '20 components · 138 tokens',
		tags: ['editorial', 'cozy', 'modern'],
		category: ShowcaseCategory.MARKETING,
		dsUrl: '/ds/e254d183-55ee-4b73-8cd6-f3071b26304a/Pixelo',
		projectUrl: '/project/f6c383fd-8ae1-4931-acf7-475098ab4ae8/Pixelo-Sample-Project',
	},
	{
		designSystemId: 'c4c25ac4-7aa4-4b20-a93f-8a91c1e29ac0',
		title: 'Ledger',
		description:
			'A Stripe-inspired dashboard system designed for analytics, billing interfaces, and operational control panels.',
		imageUrl: '/ds_showcase/ledger.png',
		hiResImageUrl: '/ds_showcase/ledger@2x.png',
		metadata: '37 components · 165 tokens',
		tags: ['analytics', 'admin', 'data'],
		category: ShowcaseCategory.DASHBOARD,
		dsUrl: '/ds/c4c25ac4-7aa4-4b20-a93f-8a91c1e29ac0/Ledger',
		projectUrl: '/project/ff9c10d3-e41c-42c4-889a-4ba90226d87d/Ledger-Sample-Project',
	},
	{
		designSystemId: '4186fe0d-63a3-484b-9ecb-dd42552320f2',
		title: 'Zenith',
		description:
			'For project management and team workflows. Prioritising clarity and scannability across dense information layouts, with a touch of fun.',
		imageUrl: '/ds_showcase/zenith.png',
		hiResImageUrl: '/ds_showcase/zenith@2x.png',
		metadata: '',
		tags: ['productivity', 'teams', 'project management'],
		category: ShowcaseCategory.SAAS,
		dsUrl: '/ds/4186fe0d-63a3-484b-9ecb-dd42552320f2/Zenith',
		projectUrl: '/project/307d80c6-dc80-4de6-9839-76b19ef1fbfb/ProjectFlow-UI',
	},
	{
		designSystemId: 'db86957b-df4c-4138-ae74-11a9f885a421',
		title: 'Ampere',
		description:
			'A dark-first, high-energy design system that targets iOS portrait screens. Built around a deep navy palette with magenta and cyan accents.',
		imageUrl: '/ds_showcase/ampere.png',
		hiResImageUrl: '/ds_showcase/ampere@2x.png',
		metadata: '',
		tags: ['mobile', 'dark mode', 'energy'],
		category: ShowcaseCategory.MOBILE,
		dsUrl: '/ds/db86957b-df4c-4138-ae74-11a9f885a421/Ampere',
		projectUrl: '/project/2cf8d766-b4a2-4047-b365-c724a1d08b57/Ampere-App-Flows',
	},
	{
		designSystemId: '9ce6b3c4-00d5-4d49-9247-d023944973a7',
		title: 'Fusion',
		description:
			'For premium mobile fintech products. Frosted surfaces, warm glows, and tight typography create a blend of luxury and trust.',
		imageUrl: '/ds_showcase/fusion.png',
		hiResImageUrl: '/ds_showcase/fusion@2x.png',
		metadata: '',
		tags: ['fintech', 'mobile', 'premium'],
		category: ShowcaseCategory.MOBILE,
		dsUrl: '/ds/9ce6b3c4-00d5-4d49-9247-d023944973a7/Fusion',
		projectUrl: '/project/1d5f338e-bb20-4ddf-9d1b-458b470604db/Fusion-Banking-App',
	},
	{
		designSystemId: '4ae366a1-d249-4914-ba1d-d2e8e5bae8fd',
		title: 'Forage',
		description:
			'For mobile meal planning and grocery shopping. Evokes a cookbook feel rather than a clinical dashboard. Warm, celebratory and utilitarian.',
		imageUrl: '/ds_showcase/forage.png',
		hiResImageUrl: '/ds_showcase/forage@2x.png',
		metadata: '',
		tags: ['food', 'mobile', 'lifestyle'],
		category: ShowcaseCategory.MOBILE,
		dsUrl: '/ds/4ae366a1-d249-4914-ba1d-d2e8e5bae8fd/Forage',
		projectUrl: '/project/c5caddb3-5744-416c-b3fe-9f850bcb1a1a/Recipe-App',
	},
];

const SHOWCASE_CATEGORIES = [
	{ value: ShowcaseCategory.ALL, label: 'All' },
	{ value: ShowcaseCategory.SAAS, label: 'SaaS' },
	{ value: ShowcaseCategory.MARKETING, label: 'Marketing' },
	{ value: ShowcaseCategory.MARKETPLACE, label: 'Marketplace' },
	{ value: ShowcaseCategory.MOBILE, label: 'Mobile' },
	{ value: ShowcaseCategory.ECOMMERCE, label: 'E-commerce' },
	{ value: ShowcaseCategory.DASHBOARD, label: 'Dashboard' },
];

//----------------------------------------------------------------------
// COMPONENT
//----------------------------------------------------------------------

const DsShowcaseModal = () => {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const dsShowcaseModal = useSelector((r: RootReducer) => r.mode.dsShowcaseModal);

	const [searchQuery, setSearchQuery] = useState('');
	const [activeCategory, setActiveCategory] = useState<ShowcaseCategory>(ShowcaseCategory.ALL);

	//----------------------------------------------------------------------
	// UTILITY
	//----------------------------------------------------------------------

	const filteredItems = useMemo(() => {
		let items = SHOWCASE_DESIGN_SYSTEMS;

		if (activeCategory !== ShowcaseCategory.ALL) {
			items = items.filter((item) => item.category === activeCategory);
		}

		if (searchQuery.trim()) {
			const q = searchQuery.toLowerCase();
			items = items.filter(
				(item) =>
					item.title.toLowerCase().includes(q) ||
					item.description.toLowerCase().includes(q) ||
					item.tags.some((tag) => tag.toLowerCase().includes(q))
			);
		}

		return items;
	}, [activeCategory, searchQuery]);

	//----------------------------------------------------------------------
	// HANDLERS
	//----------------------------------------------------------------------

	const onDismiss = () => {
		dispatch(showDsShowcaseModal(null));
	};

	const mode = dsShowcaseModal?.mode ?? 'select';

	const onUse = (item: ShowcaseDesignSystem) => {
		dsShowcaseModal?.onSelect(item);
		dispatch(showDsShowcaseModal(null));
	};

	//----------------------------------------------------------------------
	// RENDERING
	//----------------------------------------------------------------------

	return (
		<Dialog.Root modal={true} open={true} onOpenChange={(open) => !open && onDismiss()}>
			<Dialog.Portal>
				<S.Overlay onClick={onDismiss}>
					<S.ModalContainer
						onClick={(e) => e.stopPropagation()}
						role="dialog"
						aria-labelledby="ds-showcase-title"
					>
						<S.Header>
							<S.HeaderTop>
								<S.Title id="ds-showcase-title">
									{mode === 'view' ? 'Browse Design Systems' : 'Choose a Design System'}
								</S.Title>
								<S.CloseButton onClick={onDismiss} aria-label="Close">
									<Icon type={IconType.CLOSE} width={16} height={16} />
								</S.CloseButton>
							</S.HeaderTop>
							{/* <S.SearchContainer>
								<S.SearchIcon>
									<Icon type={IconType.SEARCH} width={16} height={16} />
								</S.SearchIcon>
								<S.SearchInput
									type="text"
									placeholder="Search design systems..."
									value={searchQuery}
									onChange={(e) => setSearchQuery(e.target.value)}
								/>
							</S.SearchContainer>
							<S.CategoryPills>
								{SHOWCASE_CATEGORIES.map((cat) => (
									<S.CategoryPill
										key={cat.value}
										$active={activeCategory === cat.value}
										onClick={() => setActiveCategory(cat.value)}
									>
										{cat.label}
									</S.CategoryPill>
								))}
							</S.CategoryPills> */}
						</S.Header>

						<S.Content>
							{filteredItems.length === 0 ? (
								<S.EmptyState>
									{SHOWCASE_DESIGN_SYSTEMS.length === 0
										? 'No design systems available yet.'
										: 'No design systems match your search.'}
								</S.EmptyState>
							) : (
								<S.Grid>
									{filteredItems.map((item) => (
										<ShowcaseCard
											key={item.designSystemId}
											item={item}
											onUse={onUse}
											mode={mode}
										/>
									))}
								</S.Grid>
							)}
						</S.Content>

						<S.Footer>
							<S.FooterText>
								Want to build your own?{' '}
								<S.FooterLink
									onClick={() => {
										trackDesignSystemAction('gallery_setup_ds');
										onDismiss();
										navigate(createDesignSystemPattern);
									}}
								>
									Set up a design system →
								</S.FooterLink>
							</S.FooterText>
						</S.Footer>
					</S.ModalContainer>
				</S.Overlay>
			</Dialog.Portal>
		</Dialog.Root>
	);
};

export default DsShowcaseModal;
